import { Injectable } from '@angular/core';
import { Channel, ChannelFns } from 'icreate/common/models/channel';
import { DefaultColorName, SystemColor } from 'icreate/common/models/system-color';

import { AlarmLevel } from 'icreate/common/models/alarm-level';
import {
    FLAG_UNDEFINED_COLOR,
    FlagOption,
    getAlarmColors,
    getAlarmStateColor,
} from '@service/graphs/print-gauge-graph-utils';
import { GaugeSetting, GaugeTitlePosition } from 'icreate/common/models/editor-viewer-element';
import { getDefaultScaleDivision } from 'icreate/common/models/graphs';
import { ElementAttribute } from '@src/app/models/editor';
import { CanvasService } from './canvas.service';

@Injectable({ providedIn: 'root' })
export class GaugeExtensionService {
    static readonly DIVISION_OPTIONS = [1, 3, 5, 7, 9];

    private static readonly ARC_WIDTH = 169;
    private static readonly ARC_HEIGHT = 143;
    private static readonly CENTER_X = 84.05;
    private static readonly CENTER_Y = 84;
    private static readonly OUTER_RADIUS = 84;
    private static readonly INNER_RADIUS = 73.3;
    private static readonly ARC_ANGLE = 133.4;

    private static readonly ARC_LEFT_OUTER = { x: 23.225, y: 141.634 };
    private static readonly ARC_LEFT_INNER = { x: 30.795, y: 134.071 };
    private static readonly ARC_LEFT_CAP = 'c.76.795 2.023.793 2.801.017l4.749-4.735c.782-.779.78-2.044.02-2.845';
    private static readonly ARC_RIGHT_OUTER = { x: 144.874, y: 141.635 };
    private static readonly ARC_RIGHT_INNER = { x: 137.306, y: 134.072 };
    private static readonly ARC_RIGHT_CAP = 'c-.761.801-.763 2.066.019 2.845l4.748 4.735c.779.776 2.041.778 2.801-.017';

    private static readonly PIN_POINTS = [
        [7.247, 41.002],
        [7.256, 41.106],
        [7.187, 41.186],
        [3.815, 45.098],
        [3.618, 45.326],
        [3.431, 45.09],
        [0.318, 41.178],
        [0.257, 41.102],
        [0.265, 41.002],
        [3.377, 0.023],
        [3.875, 0.022],
    ];
    private static readonly PIN_PIVOT_X = 3.8;
    private static readonly PIN_PIVOT_Y = 41;

    private static readonly PATH_DECIMALS = 3;
    private static readonly MINOR_TICKS = 5;
    private static readonly MAJOR_TICK = { from: 70, to: 64, width: 0.8 };
    private static readonly MINOR_TICK = { from: 70, to: 67, width: 0.5 };
    private static readonly LABEL_RADIUS = 58;
    private static readonly LABEL_FONT_SIZE = 5.5;
    private static readonly VALUE_Y = 115;
    private static readonly VALUE_FONT_SIZE = 13;
    private static readonly STATUS_Y = 129;
    private static readonly STATUS_FONT_SIZE = 9;
    private static readonly TITLE_FONT_SIZE = 12;
    private static readonly TITLE_LINE_HEIGHT = 15;
    private static readonly TITLE_GAP = 8;
    private static readonly FONT_FAMILY = 'NotoSans';

    private static readonly DEFAULT_ARC_COLOR = '#2c3a48';
    private static readonly DEFAULT_PIN_COLOR = '#5b6876';
    private static readonly TICK_COLOR = 'rgba(255, 255, 255, 0.4)';
    private static readonly VALUE_COLOR = '#ffffff';
    private static readonly SUB_TEXT_COLOR = 'rgba(255, 255, 255, 0.7)';

    private static readonly DEFAULT_WIDTH = 400;
    private static readonly NORMAL_STATUS_TEXT = 'Normal';

    private static readonly PREVIEW_MIN = 0;
    private static readonly PREVIEW_MAX = 100;
    private static readonly PREVIEW_VALUE = 0;

    constructor(private canvasService: CanvasService) {}

    setChannel(channel: Channel, element: SVGGElement, systemColors: SystemColor[]): void {
        this.render(element, channel, systemColors);
    }

    render(element: SVGGElement, channel?: Channel, systemColors: SystemColor[] = []): void {
        const setting = this.getSetting(element, channel);
        const oldRect = element.querySelector('rect');
        const width = oldRect ? Number(oldRect.getAttribute('width')) : GaugeExtensionService.DEFAULT_WIDTH;
        const oldX = oldRect ? Number(oldRect.getAttribute('x')) : 0;
        const oldY = oldRect ? Number(oldRect.getAttribute('y')) : 0;

        Array.from(element.children).forEach(child => {
            if (child !== oldRect) element.removeChild(child);
        });

        const min = channel
            ? this.scaleValue(channel, channel.fcuData.measurableRange.min)
            : GaugeExtensionService.PREVIEW_MIN;
        const max = channel
            ? this.scaleValue(channel, channel.fcuData.measurableRange.max)
            : GaugeExtensionService.PREVIEW_MAX;
        const decimalPlaces = this.getDecimalPlaces(channel);
        const scaleValues =
            setting.isCustom && setting.scaleValues.length === setting.divisions + 2
                ? setting.scaleValues
                : this.getAutoScaleValues(min, max, setting.divisions + 1);
        const alarmColors = getAlarmColors(systemColors);
        const axisLineColors: [number, string][] = channel
            ? this.buildAxisLineColors(channel, min, max, alarmColors)
            : [[1, GaugeExtensionService.DEFAULT_ARC_COLOR]];

        const content = this.createElement('g', {});
        element.appendChild(content);

        const body = this.createElement('g', {});
        content.appendChild(body);
        let previous = 0;
        axisLineColors.forEach(([percentage, color], i) => {
            const current = i === axisLineColors.length - 1 ? 1 : percentage;
            const from = this.valueToAngle(min + (max - min) * previous, scaleValues);
            const to = this.valueToAngle(min + (max - min) * current, scaleValues);
            body.appendChild(this.createElement('path', { d: this.buildArcPath(from, to), fill: color }));
            previous = current;
        });
        this.appendTicks(body, scaleValues, decimalPlaces);

        const value = GaugeExtensionService.PREVIEW_VALUE;
        const status = channel ? this.getStatus(channel, value, alarmColors) : null;
        const pinAngle = this.valueToAngle(channel ? this.scaleValue(channel, value) : min, scaleValues);
        body.appendChild(
            this.createElement('path', {
                d: this.buildPinPath(pinAngle),
                fill: status?.color ?? GaugeExtensionService.DEFAULT_PIN_COLOR,
            })
        );
        if (channel) {
            this.appendValueText(body, setting, channel, this.scaleValue(channel, value), decimalPlaces, status);
        }

        const title = setting.title || channel?.opsData?.name || '';
        const titleHeight = setting.nameDisplay && title ? this.appendTitle(content, title) : 0;
        const titleArea = titleHeight ? titleHeight + GaugeExtensionService.TITLE_GAP : 0;
        const isTop = setting.titlePosition === GaugeTitlePosition.TOP;
        const titleText = content.querySelector('text[data-gauge-title]');
        if (isTop) body.setAttribute('transform', `translate(0 ${titleArea})`);
        titleText?.setAttribute(
            'transform',
            `translate(0 ${isTop ? 0 : GaugeExtensionService.ARC_HEIGHT + GaugeExtensionService.TITLE_GAP})`
        );

        const ratio = width / GaugeExtensionService.ARC_WIDTH;
        oldRect?.setAttribute('height', String((GaugeExtensionService.ARC_HEIGHT + titleArea) * ratio));
        content.setAttribute('transform', `translate(${oldX} ${oldY}) scale(${ratio})`);
        this.refreshSelectionFrame(element);
    }

    getSetting(element: Element, channel?: Channel): GaugeSetting {
        const scaleValues = element.getAttribute(ElementAttribute.gaugeScaleValues);
        return {
            divisions:
                Number(element.getAttribute(ElementAttribute.gaugeDivisions)) || getDefaultScaleDivision(channel) - 1,
            isCustom: element.getAttribute(ElementAttribute.gaugeCustomDivisions) === 'true',
            scaleValues: scaleValues ? JSON.parse(scaleValues) : [],
            unitDisplay: element.getAttribute(ElementAttribute.gaugeUnitDisplay) !== 'false',
            valueDisplay: element.getAttribute(ElementAttribute.gaugeValueDisplay) !== 'false',
            nameDisplay: element.getAttribute(ElementAttribute.gaugeNameDisplay) !== 'false',
            title: element.getAttribute(ElementAttribute.gaugeTitle) ?? '',
            titlePosition:
                (element.getAttribute(ElementAttribute.gaugeTitlePosition) as GaugeTitlePosition) ??
                GaugeTitlePosition.BOTTOM,
        };
    }

    getAutoScaleValues(min: number, max: number, divisions: number): number[] {
        const step = (max - min) / divisions;
        return Array.from({ length: divisions + 1 }, (_, i) => min + step * i);
    }

    getDecimalPlaces(channel?: Channel): number {
        const scale = channel?.fcuData.scale || 1;
        return scale === 1 ? 0 : Math.round(Math.log10(scale));
    }

    private scaleValue(channel: Channel, value: number) {
        return value / channel.fcuData.scale;
    }

    private buildAxisLineColors(channel: Channel, rangeMin: number, rangeMax: number, alarmColors): [number, string][] {
        const totalRange = Math.abs(rangeMax - rangeMin);
        const ranges = this.calculateRanges(channel, rangeMin, rangeMax);

        const colorArr: [number, string][] = [];
        let previewRange = 0;
        let rangeKeys = Array.from(ranges.keys());

        for (let i = 0; i < rangeKeys.length; i++) {
            let range = ranges.get(rangeKeys[i]);
            const width = range.max - range.min;
            if (width > 0) {
                let percentage = previewRange + (range.max - range.min) / totalRange;
                let color = getAlarmStateColor(rangeKeys[i], channel, alarmColors);
                if (rangeKeys[i] === FlagOption.NORMAL) color = FLAG_UNDEFINED_COLOR;
                colorArr.push([percentage, color]);
                previewRange = percentage;
            }
        }

        return colorArr;
    }

    private calculateRanges(channel: Channel, rangeMin: number, rangeMax: number) {
        const fcuAlarms = channel.fcuData;
        const rangeOffset = 1 / channel.fcuData.scale;
        const rangeMap = new Map();

        const toRange = (min: number, max: number, zeroOut: boolean = false): { min: number; max: number } => {
            if (max - min == 0 && !zeroOut) max += rangeOffset;
            return zeroOut ? { min: 0, max: 0 } : { min, max };
        };

        const gapRange = (min: number, max: number): { min: number; max: number } => {
            if (max - min <= rangeOffset) return { min: 0, max: 0 };
            if (max != rangeMax) max -= rangeOffset;
            return { min, max };
        };

        const veryLow = toRange(
            rangeMin,
            this.scaleValue(channel, fcuAlarms.alarmSettingVeryLow.setPoint),
            !fcuAlarms.alarmSettingVeryLow.enable
        );
        const low = toRange(
            fcuAlarms.alarmSettingVeryLow.enable ? veryLow.max : rangeMin,
            this.scaleValue(channel, fcuAlarms.alarmSettingLow.setPoint),
            !fcuAlarms.alarmSettingLow.enable
        );
        const veryHigh = toRange(
            this.scaleValue(channel, fcuAlarms.alarmSettingVeryHigh.setPoint),
            rangeMax,
            !fcuAlarms.alarmSettingVeryHigh.enable
        );
        const high = toRange(
            this.scaleValue(channel, fcuAlarms.alarmSettingHigh.setPoint),
            fcuAlarms.alarmSettingVeryHigh.enable ? veryHigh.min : rangeMax,
            !fcuAlarms.alarmSettingHigh.enable
        );

        const hasNormalRange = fcuAlarms.normalRange.min !== 0 || fcuAlarms.normalRange.max !== 0;
        const normal = toRange(
            this.scaleValue(channel, fcuAlarms.normalRange.min),
            this.scaleValue(channel, fcuAlarms.normalRange.max),
            !hasNormalRange
        );
        const withoutNormalRange = ((high.min || rangeMax) - (low.max || rangeMin)) / 2;

        const lowToNormalGap = gapRange(
            low.max || rangeMin,
            hasNormalRange ? normal.min : (low.max || rangeMin) + withoutNormalRange
        );

        const normalToHighGap = gapRange(hasNormalRange ? normal.max : lowToNormalGap.max, high.min || rangeMax);

        rangeMap.set(FlagOption.LOW_LOW, veryLow);
        rangeMap.set(FlagOption.LOW, low);
        rangeMap.set('lowToNormal', lowToNormalGap);
        rangeMap.set(FlagOption.NORMAL, normal);
        rangeMap.set('normalToHigh', normalToHighGap);
        rangeMap.set(FlagOption.HIGH, high);
        rangeMap.set(FlagOption.HIGH_HIGH, veryHigh);

        return rangeMap;
    }

    removeChannel(element: SVGGElement): void {
        this.render(element);
    }

    private valueToAngle(value: number, scaleValues: number[]): number {
        const last = scaleValues.length - 1;
        if (value <= scaleValues[0]) return -GaugeExtensionService.ARC_ANGLE;
        if (value >= scaleValues[last]) return GaugeExtensionService.ARC_ANGLE;
        const index = scaleValues.findIndex((_, i) => i < last && value <= scaleValues[i + 1]);
        const ratio = (value - scaleValues[index]) / (scaleValues[index + 1] - scaleValues[index]);
        return -GaugeExtensionService.ARC_ANGLE + ((index + ratio) * GaugeExtensionService.ARC_ANGLE * 2) / last;
    }

    private refreshSelectionFrame(element: SVGGElement) {
        const canvas = this.canvasService.getCanvas();
        canvas.call('changed', [element]);
        const selector = canvas.selectorManager?.requestSelector(element);
        if (selector) {
            selector.resize();
        }
    }

    private getStatus(channel: Channel, value: number, alarmColors): { text: string; color: string } {
        const alarmLevel = ChannelFns.toAlarmLevel(channel, ChannelFns.getCalculatedAlarmLevel(channel, value));
        const alarmLevelMap = {
            [AlarmLevel.Caution]: DefaultColorName.CAUTION,
            [AlarmLevel.Warning]: DefaultColorName.WARNING,
            [AlarmLevel.Alarm]: DefaultColorName.ALARM,
        };
        const colorName = alarmLevelMap[alarmLevel];
        if (!colorName) return { text: GaugeExtensionService.NORMAL_STATUS_TEXT, color: FLAG_UNDEFINED_COLOR };
        return { text: colorName, color: alarmColors[colorName] };
    }

    private buildArcPath(from: number, to: number): string {
        const isStart = from <= -GaugeExtensionService.ARC_ANGLE;
        const isEnd = to >= GaugeExtensionService.ARC_ANGLE;
        const largeArc = to - from > 180 ? 1 : 0;
        const outerFrom = isStart
            ? GaugeExtensionService.ARC_LEFT_OUTER
            : this.polar(from, GaugeExtensionService.OUTER_RADIUS);
        const outerTo = isEnd
            ? GaugeExtensionService.ARC_RIGHT_OUTER
            : this.polar(to, GaugeExtensionService.OUTER_RADIUS);
        const innerFrom = isStart
            ? GaugeExtensionService.ARC_LEFT_INNER
            : this.polar(from, GaugeExtensionService.INNER_RADIUS);
        const innerTo = isEnd
            ? GaugeExtensionService.ARC_RIGHT_INNER
            : this.polar(to, GaugeExtensionService.INNER_RADIUS);

        return [
            `M${outerTo.x} ${outerTo.y}`,
            `A${GaugeExtensionService.OUTER_RADIUS} ${GaugeExtensionService.OUTER_RADIUS} 0 ${largeArc} 0 ${outerFrom.x} ${outerFrom.y}`,
            isStart ? GaugeExtensionService.ARC_LEFT_CAP : `L${innerFrom.x} ${innerFrom.y}`,
            `A${GaugeExtensionService.INNER_RADIUS} ${GaugeExtensionService.INNER_RADIUS} 0 ${largeArc} 1 ${innerTo.x} ${innerTo.y}`,
            isEnd ? GaugeExtensionService.ARC_RIGHT_CAP : '',
            'Z',
        ].join('');
    }

    private buildPinPath(angle: number): string {
        const radian = (angle * Math.PI) / 180;
        const cos = Math.cos(radian);
        const sin = Math.sin(radian);
        const points = GaugeExtensionService.PIN_POINTS.map(([x, y], i) => {
            const dx = x - GaugeExtensionService.PIN_PIVOT_X;
            const dy = y - GaugeExtensionService.PIN_PIVOT_Y;
            return `${i ? 'L' : 'M'}${this.round(GaugeExtensionService.CENTER_X + dx * cos - dy * sin)} ${this.round(GaugeExtensionService.CENTER_Y + dx * sin + dy * cos)}`;
        });
        return `${points.join('')}Z`;
    }

    private appendTicks(parent: SVGElement, scaleValues: number[], decimalPlaces: number): void {
        const divisions = scaleValues.length - 1;
        const tickCount = divisions * GaugeExtensionService.MINOR_TICKS;
        for (let i = 0; i <= tickCount; i++) {
            const isMajor = i % GaugeExtensionService.MINOR_TICKS === 0;
            const tick = isMajor ? GaugeExtensionService.MAJOR_TICK : GaugeExtensionService.MINOR_TICK;
            const angle = -GaugeExtensionService.ARC_ANGLE + (i * GaugeExtensionService.ARC_ANGLE * 2) / tickCount;
            const from = this.polar(angle, tick.from);
            const to = this.polar(angle, tick.to);
            parent.appendChild(
                this.createElement('line', {
                    x1: from.x,
                    y1: from.y,
                    x2: to.x,
                    y2: to.y,
                    stroke: GaugeExtensionService.TICK_COLOR,
                    'stroke-width': tick.width,
                })
            );
            if (!isMajor) continue;

            const labelPoint = this.polar(angle, GaugeExtensionService.LABEL_RADIUS);
            const label = this.createText(
                scaleValues[i / GaugeExtensionService.MINOR_TICKS].toFixed(decimalPlaces),
                labelPoint.y,
                GaugeExtensionService.LABEL_FONT_SIZE,
                GaugeExtensionService.TICK_COLOR
            );
            label.setAttribute('x', String(labelPoint.x));
            parent.appendChild(label);
        }
    }

    private appendValueText(
        parent: SVGElement,
        setting: GaugeSetting,
        channel: Channel,
        value: number,
        decimalPlaces: number,
        status: { text: string; color: string }
    ): void {
        const unit = channel.opsData.measurementUnit;
        const parts = [
            setting.valueDisplay ? value.toFixed(decimalPlaces) : '',
            setting.unitDisplay ? unit : '',
        ].filter(Boolean);
        if (parts.length) {
            const text = this.createText(
                parts.join(' '),
                GaugeExtensionService.VALUE_Y,
                GaugeExtensionService.VALUE_FONT_SIZE,
                GaugeExtensionService.VALUE_COLOR
            );
            text.setAttribute('font-weight', '600');
            parent.appendChild(text);
        }
        if (setting.valueDisplay) {
            parent.appendChild(
                this.createText(
                    status.text,
                    GaugeExtensionService.STATUS_Y,
                    GaugeExtensionService.STATUS_FONT_SIZE,
                    GaugeExtensionService.SUB_TEXT_COLOR
                )
            );
        }
    }

    private appendTitle(parent: SVGElement, title: string): number {
        const text = this.createText(
            '',
            0,
            GaugeExtensionService.TITLE_FONT_SIZE,
            GaugeExtensionService.SUB_TEXT_COLOR
        );
        text.setAttribute('data-gauge-title', 'true');
        parent.appendChild(text);

        let line = this.appendTitleLine(text, 0);
        title.split(' ').forEach(word => {
            const previous = line.textContent;
            line.textContent = previous ? `${previous} ${word}` : word;
            if (previous && line.getComputedTextLength() > GaugeExtensionService.ARC_WIDTH) {
                line.textContent = previous;
                line = this.appendTitleLine(text, text.childNodes.length);
                line.textContent = word;
            }
        });
        return text.childNodes.length * GaugeExtensionService.TITLE_LINE_HEIGHT;
    }

    private appendTitleLine(text: SVGTextElement, index: number): SVGTSpanElement {
        const line = this.createElement('tspan', {
            x: GaugeExtensionService.CENTER_X,
            y: GaugeExtensionService.TITLE_LINE_HEIGHT * index + GaugeExtensionService.TITLE_FONT_SIZE / 2,
        }) as SVGTSpanElement;
        text.appendChild(line);
        return line;
    }

    private createText(content: string, y: number, fontSize: number, color: string): SVGTextElement {
        const text = this.createElement('text', {
            x: GaugeExtensionService.CENTER_X,
            y,
            fill: color,
            'font-size': fontSize,
            'font-family': GaugeExtensionService.FONT_FAMILY,
            'text-anchor': 'middle',
            'dominant-baseline': 'central',
        }) as SVGTextElement;
        text.textContent = content;
        return text;
    }

    private createElement(tag: string, attributes: Record<string, string | number>): SVGElement {
        const element = document.createElementNS('http://www.w3.org/2000/svg', tag);
        Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, String(value)));
        return element;
    }

    private polar(angle: number, radius: number): { x: number; y: number } {
        const radian = (angle * Math.PI) / 180;
        return {
            x: this.round(GaugeExtensionService.CENTER_X + radius * Math.sin(radian)),
            y: this.round(GaugeExtensionService.CENTER_Y - radius * Math.cos(radian)),
        };
    }

    private round(value: number): number {
        return Number(value.toFixed(GaugeExtensionService.PATH_DECIMALS));
    }
}
